import { Link, useNavigate } from "react-router-dom";
import { format } from "date-fns";
import {
  Bell,
  Search,
  Menu,
  FileText,
  AlertTriangle,
  UserPlus,
  Receipt,
  CheckCircle,
  XCircle,
  MessageSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useNotifications,
  getNotificationColor,
} from "@/hooks/useNotifications";
import { useLanguage } from "@/contexts/LanguageContext";
import { dateFnsLocaleFor } from "@/lib/locale";
import { useAppShellTranslations } from "@/components/app/i18n/shell";

interface AppHeaderProps {
  /** Abre o drawer do sidebar no mobile (< lg, S16). */
  onOpenMenu: () => void;
}

const iconMap: Record<string, React.ElementType> = {
  REPORT_COMPLETED: FileText,
  RISK_RATING_CHANGED: AlertTriangle,
  SECTOR_RISK_CHANGED: AlertTriangle,
  COMPANY_RATING_CHANGED: AlertTriangle,
  urba_monitoring_change: AlertTriangle,
  WELCOME_NEW_USER: UserPlus,
  INVOICE_GENERATED: Receipt,
  PAYMENT_SUCCESS: CheckCircle,
  PAYMENT_FAILED: XCircle,
  SUPPORT_MESSAGE: MessageSquare,
};

const MENU_BUTTON_CLASS =
  "flex h-9 w-9 shrink-0 items-center justify-center rounded-[8px] text-tr-ink outline-none " +
  "hover:bg-tr-canvas focus-visible:ring-2 focus-visible:ring-tr-mint lg:hidden";

export function AppHeader({ onOpenMenu }: AppHeaderProps) {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useAppShellTranslations();
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useNotifications();

  const recentNotifications = notifications.slice(0, 5);

  return (
    <header className="flex h-[65px] shrink-0 items-center justify-between gap-4 border-b border-tr-border bg-white px-4 lg:px-6">
      {/* Menu (mobile): abre o drawer do sidebar */}
      <button
        type="button"
        onClick={onOpenMenu}
        aria-label={t.sidebar.openMenu}
        className={MENU_BUTTON_CLASS}
      >
        <Menu className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
      </button>

      {/* Busca */}
      <label className="flex w-full max-w-[420px] items-center gap-2.5 rounded-lg bg-tr-canvas px-3 py-[9px] focus-within:ring-1 focus-within:ring-tr-mint">
        <Search className="h-4 w-4 shrink-0 text-tr-muted" strokeWidth={1.75} />
        <span className="sr-only">{t.header.searchLabel}</span>
        <input
          type="search"
          placeholder={t.header.searchPlaceholder}
          className="w-full bg-transparent text-sm text-tr-ink outline-none placeholder:text-tr-muted"
        />
      </label>

      <div className="flex items-center gap-5">
        {/* Notificações */}
        <DropdownMenu>
          <DropdownMenuTrigger
            className="relative flex h-7 w-7 items-center justify-center rounded-full text-tr-ink outline-none focus-visible:ring-1 focus-visible:ring-tr-mint"
            aria-label={
              unreadCount > 0
                ? t.header.notificationsWithUnread.replace(
                    "{count}",
                    String(unreadCount),
                  )
                : t.header.notifications
            }
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full border-[1.5px] border-tr-ink">
              <Bell className="h-3.5 w-3.5" strokeWidth={2} />
            </span>
            {unreadCount > 0 && (
              <span className="absolute -top-1 left-4 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-tr-risk-high px-1 text-[10px] font-medium text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="flex items-center justify-between">
              {t.header.notifications}
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                  onClick={() => markAllAsRead()}
                >
                  {t.header.markAllRead}
                </Button>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {recentNotifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                {t.header.noNotifications}
              </div>
            ) : (
              <>
                {recentNotifications.map((notification) => {
                  const IconComponent =
                    iconMap[notification.notification_type] || Bell;
                  const colorClass = getNotificationColor(
                    notification.notification_type,
                  );
                  const metadata = notification.metadata as {
                    ticket_id?: string;
                  } | null;
                  // reference_id vem do banco mas ainda não está nos tipos gerados
                  const referenceId = (
                    notification as { reference_id?: string | null }
                  ).reference_id;

                  return (
                    <DropdownMenuItem
                      key={notification.id}
                      className={`flex cursor-pointer items-start gap-3 p-3 ${!notification.is_read ? "bg-muted/50" : ""}`}
                      onClick={() => {
                        if (!notification.is_read) markAsRead(notification.id);
                        if (
                          notification.notification_type ===
                            "SUPPORT_MESSAGE" &&
                          metadata?.ticket_id
                        ) {
                          navigate(`/app/support/${metadata.ticket_id}`);
                        } else if (
                          (notification.notification_type ===
                            "urba_monitoring_change" ||
                            notification.notification_type ===
                              "COMPANY_RATING_CHANGED") &&
                          referenceId
                        ) {
                          navigate(`/app/companies/${referenceId}`);
                        }
                      }}
                    >
                      <div className={`mt-0.5 ${colorClass}`}>
                        <IconComponent className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">
                          {notification.title}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {notification.message}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {notification.created_at &&
                            format(
                              new Date(notification.created_at),
                              "dd MMM HH:mm",
                              {
                                locale: dateFnsLocaleFor(language),
                              },
                            )}
                        </p>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link
                    to="/app/notifications"
                    className="w-full text-center text-sm text-mint hover:text-mint-dark"
                  >
                    {t.header.viewAllNotifications}
                  </Link>
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
